import {
  collection,
  onSnapshot,
  query,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { collectionData } from "rxfire/firestore";
import { combineLatest, Subscription } from "rxjs";
import { map } from "rxjs/operators";

export const useUserDocuments = (userId: string) =>
  defineStore(`/users/${userId}/documents`, () => {
    const appUserDataStore = useAppUserData();
    const { numGradedDocuments, showOtherUsersSubmissions } =
      storeToRefs(appUserDataStore);

    const hasIniitalDocumentsDataLoaded = ref(false);

    const ungradedDocuments = ref<SubmittedDocuments>([]);
    const otherUserClassroomUngradedDocuments = ref<SubmittedDocuments>([]);

    const subscription = ref<Unsubscribe | undefined>();

    const isInitialized = ref(false);

    const initialize = () => {
      if (isInitialized.value) return;

      const db = useFirestore();
      const documentsRef = collection(db, `/documents`);
      const documentsQuery = query(
        documentsRef,
        where("userId", "==", userId),
        where("state", "not-in", [
          DocumentState.studentReview,
          DocumentState.graded,
          DocumentState.archived,
          DocumentState.ignored,
        ])
      );

      if (subscription.value) {
        subscription.value();
      }

      subscription.value = onSnapshot(documentsQuery, (snapshot) => {
        ungradedDocuments.value = snapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          } as SubmittedDocument;
        });

        hasIniitalDocumentsDataLoaded.value = true;
      });

      isInitialized.value = true;
    };

    const otherUserClassroomSubmissionsSubscription = ref<
      Subscription | undefined
    >();

    const initializeOtherUserClassroomSubmissions = ({
      classroomIds,
    }: {
      classroomIds: string[];
    }) => {
      otherUserClassroomSubmissionsSubscription.value?.unsubscribe();

      const db = useFirestore();
      const submissionsRef = collection(db, `/documents`);

      // Split classroomIds into chunks of 10
      const chunkSize = 10;
      const classroomIdChunks = [];
      for (let i = 0; i < classroomIds.length; i += chunkSize) {
        classroomIdChunks.push(classroomIds.slice(i, i + chunkSize));
      }

      // Create an observable for each chunk
      const observables = classroomIdChunks.map((chunk) => {
        const submissionsQuery = query(
          submissionsRef,
          where("classroomId", "in", chunk),
          where("state", "in", [
            DocumentState.submitted,
            DocumentState.notImported,
            DocumentState.conversionFailed,
            DocumentState.evaluating,
            DocumentState.pending,
            DocumentState.uploading,
            DocumentState.googlePermissionError,
            DocumentState.unsupportedFormat,
            DocumentState.grading,
            DocumentState.processing,
            DocumentState.converting,
          ])
        );

        return collectionData(submissionsQuery, {
          idField: "id",
        }).pipe(
          map((submissions) => {
            return submissions.map((submission) => {
              return {
                ...submission,
                id: submission.id,
              } as SubmittedDocument;
            });
          })
        );
      });

      // Combine all observables into one
      const combinedObservable = combineLatest(observables).pipe(
        map((results) => results.flat())
      );

      // Subscribe to the combined observable
      otherUserClassroomSubmissionsSubscription.value =
        combinedObservable.subscribe((submissions) => {
          // Handle the combined submissions here
          otherUserClassroomUngradedDocuments.value = submissions;
        });

      // Store the subscription for cleanup
    };

    const allUngradedDocuments = computed(() => {
      return [
        ...ungradedDocuments.value,
        ...otherUserClassroomUngradedDocuments.value,
      ];
    });

    const homePageUngradedDocuments = computed(() => {
      if (showOtherUsersSubmissions.value == true)
        return allUngradedDocuments.value;

      return ungradedDocuments.value;
    });

    const classroomUngradedDocuments = computed(() => {
      return (classroomId: string) => {
        return allUngradedDocuments.value.filter(
          (doc) => doc.classroomId === classroomId
        );
      };
    });

    const assignmentUngradedDocuments = computed(() => {
      return (assignmentId: string) => {
        return allUngradedDocuments.value.filter(
          (doc) => doc.assignmentId === assignmentId
        );
      };
    });

    const hasOtherUserClassroomSubmissions = computed(() => {
      return otherUserClassroomUngradedDocuments.value.length > 0;
    });

    return {
      initialize,
      allUngradedDocuments,
      homePageUngradedDocuments,
      hasOtherUserClassroomSubmissions,
      classroomUngradedDocuments,
      assignmentUngradedDocuments,
      numGradedDocuments,
      hasIniitalDocumentsDataLoaded,
      initializeOtherUserClassroomSubmissions,
    };
  });
